import { useEffect, useMemo } from 'react';
import { z } from 'zod';

import {
    faCalendar,
    faCalendarCheck,
    faCalendarHeart,
    faClock,
    faMapLocationDot,
    faRepeat,
    faTriangleExclamation,
    faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { Box, Button, Divider, Link as MuiLink, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';

import LicensedCaregiverDisplay from 'src/components/LicensedCaregiverDisplay';
import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import StyledIcon from 'src/components/StyledIcon';
import TeamAdminDisplay from 'src/components/TeamAdminDisplay';
import TeamMemberAvatar from 'src/components/TeamMemberAvatar';
import { TeamRoles } from 'src/constants/TeamRoles';
import { VisitStatuses, VisitStatusLabels } from 'src/constants/Visit';
import { useAnalytics } from 'src/contexts/AnalyticsContext';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { useVisitScheduleDisplay } from 'src/hooks/useVisitScheduleDisplay';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import { useGetVisitByRidQuery, useGetVisitScheduleQuery } from 'src/store/sageHomeApi';

const detailSearchSchema = z.object({
    visitRid: z.string().optional(),
    visitStartInstant: z.coerce.number().optional(),
});

export const Route = createFileRoute('/(app)/visits/detail/$visitScheduleRid')({
    component: VisitDetailComponent,
    validateSearch: zodValidator(detailSearchSchema),
});

const PageName = 'Visit Detail';

function VisitDetailComponent() {
    const navigate = useNavigate();
    const { visitScheduleRid } = useParams({ from: '/(app)/visits/detail/$visitScheduleRid' });
    const { visitRid, visitStartInstant } = Route.useSearch();
    const { palette, spacing } = useTheme();

    const { trackPageView, trackEvent } = useAnalytics();
    const { careRecipient, currentRecipientAddress, currentUser, getTeamMemberDetails } =
        useTeamMemberData();

    const {
        data: currentSchedule,
        isLoading: isLoadingSchedule,
        error: visitScheduleError,
    } = useGetVisitScheduleQuery(
        {
            careRecipientRid: careRecipient?.teamMemberRid || '',
            visitScheduleRid,
        },
        { skip: !visitScheduleRid || !careRecipient?.teamMemberRid }
    );

    const {
        data: currentVisit,
        isLoading: isLoadingVisit,
        error: visitError,
    } = useGetVisitByRidQuery(
        {
            careRecipientRid: careRecipient?.teamMemberRid || '',
            visitRid: visitRid || '',
        },
        { skip: !visitRid || !careRecipient?.teamMemberRid }
    );

    const isLoading = isLoadingSchedule || isLoadingVisit;
    const isError = visitScheduleError && visitError;

    // Get caregiver info if caregiverRid is available
    const caregiverRid = currentVisit?.caregiverRid || currentSchedule?.caregiverRid || '';
    const caregiver = getTeamMemberDetails(caregiverRid);
    const isVisitCaregiver = currentUser?.teamMemberRid === caregiver?.teamMemberRid;
    const isAwaitingSummary = currentVisit?.visitStatus === VisitStatuses.AWAITING_SUMMARY;
    const isInProgress = currentVisit?.visitStatus === VisitStatuses.IN_PROGRESS;
    const isTeamAdmin = currentUser?.isTeamAdmin;

    const shouldShowEditButton = useMemo(() => {
        if (!isTeamAdmin) return false;

        // Hide if non-recurring visit is in the past
        const visitStartTime = currentVisit?.visitStartInstant;
        const isNonRecurring = !currentSchedule?.cronExpression;
        if (isNonRecurring && visitStartTime && visitStartTime < Date.now()) {
            return false;
        }

        return true;
    }, [isTeamAdmin, currentSchedule?.cronExpression, currentVisit?.visitStartInstant]);

    // Create a derived visit object if we have visitStartInstant from search params but no visitRid
    const syntheticVisit = useMemo(() => {
        if (!currentVisit && visitStartInstant && currentSchedule) {
            return {
                ...currentSchedule,
                visitStartInstant,
            };
        }
        return null;
    }, [currentVisit, visitStartInstant, currentSchedule]);

    // Prefer visit if available, otherwise use derived visit or schedule
    const { startDateString, timeRangeString, repeatingDays, status } = useVisitScheduleDisplay(
        currentVisit || syntheticVisit || currentSchedule
    );

    const handleMessageCaregiver = () => {
        trackEvent('Message Caregiver Clicked', { caregiverRid: caregiver?.teamMemberRid });

        if (caregiver?.phoneNumber) {
            window.location.href = `sms:${caregiver.phoneNumber}`;
        }
    };

    const handleAddVisitSummary = () => {
        trackEvent('Add Visit Summary Clicked', { visitRid: currentVisit?.visitRid });

        navigate({
            to: '/visits/summary/add/$visitRid',
            params: { visitRid: currentVisit?.visitRid || '' },
        });
    };

    const { statusIcon, statusColor, statusLightColor } = useMemo(() => {
        const statusConfig = {
            [VisitStatuses.SCHEDULED]: {
                icon: faCalendarCheck,
                color: palette.gray?.[900],
                lightColor: palette.gray?.[300],
            },
            [VisitStatuses.IN_PROGRESS]: {
                icon: faCalendarHeart,
                color: palette.green?.[800],
                lightColor: palette.green?.[400],
            },
            [VisitStatuses.AWAITING_SUMMARY]: {
                icon: faTriangleExclamation,
                color: palette.orange?.[900],
                lightColor: palette.orange?.[300],
            },
            [VisitStatuses.COMPLETED]: {
                icon: faCalendarCheck,
                color: palette.gray?.[900],
                lightColor: palette.gray?.[300],
            },
            [VisitStatuses.CANCELED]: {
                icon: faXmark,
                color: palette.error?.main,
                lightColor: palette.error?.light,
            },
        };

        const config = statusConfig[status || VisitStatuses.SCHEDULED];
        return {
            statusIcon: config.icon,
            statusColor: config.color,
            statusLightColor: config.lightColor,
        };
    }, [status, palette]);

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InnerMaxWidthContainer>
            <PageHeader pageTitle="Care Visit" />
            {isLoading ? (
                <Typography variant="body1">Loading...</Typography>
            ) : isError ? (
                <Typography color="error" variant="body1">
                    There was an error loading the visit. Please try again.
                </Typography>
            ) : (
                // Visit Details if not completed
                <Stack sx={{ gap: 4 }}>
                    {caregiver && (
                        <Stack gap={1} sx={{ alignItems: 'center' }}>
                            <TeamMemberAvatar teamMember={caregiver || null} size="large" />
                            <Stack sx={{ alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {caregiver.firstName} {caregiver.lastName}
                                </Typography>
                                {caregiver.isTeamAdmin && <TeamAdminDisplay size="small" />}
                                {caregiver.role === TeamRoles.LicensedCaregiver && (
                                    <LicensedCaregiverDisplay size="small" />
                                )}
                            </Stack>
                        </Stack>
                    )}

                    <Stack sx={{ gap: 1 }}>
                        {/* Visit Detail title and edit button */}
                        <Stack
                            direction="row"
                            sx={{
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Visit Details
                            </Typography>
                            {shouldShowEditButton && visitScheduleRid && (
                                <Link
                                    to="/visits/edit/$visitScheduleRid"
                                    params={{ visitScheduleRid }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600, color: 'primary.main' }}
                                    >
                                        Edit
                                    </Typography>
                                </Link>
                            )}
                        </Stack>

                        <Stack
                            sx={{
                                border: '1px solid',
                                borderColor: 'gray.300',
                                borderRadius: spacing(1.5),
                                backgroundColor: 'common.white',
                            }}
                            divider={<Divider />}
                        >
                            {/* Status */}
                            {status && (
                                <Stack
                                    direction="row"
                                    sx={{
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 2.5,
                                        gap: 2,
                                    }}
                                >
                                    <Box>
                                        <Typography variant="body2">Status</Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                                color: statusColor,
                                            }}
                                        >
                                            {VisitStatusLabels[status]}
                                        </Typography>
                                    </Box>
                                    <StyledIcon
                                        icon={statusIcon}
                                        backgroundColor={statusLightColor}
                                        iconColor={statusColor}
                                    />
                                </Stack>
                            )}

                            {/* Date */}
                            {startDateString && (
                                <Stack
                                    direction="row"
                                    sx={{
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 2.5,
                                        gap: 2,
                                    }}
                                >
                                    <Box>
                                        <Typography variant="body2">Date</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {startDateString}
                                        </Typography>
                                    </Box>
                                    <StyledIcon icon={faCalendar} />
                                </Stack>
                            )}
                            {/* Time */}
                            <Stack
                                direction="row"
                                sx={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    p: 2.5,
                                    gap: 2,
                                }}
                            >
                                <Box>
                                    <Typography variant="body2">Time</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {timeRangeString}
                                    </Typography>
                                </Box>
                                <StyledIcon icon={faClock} />
                            </Stack>

                            {/* Address */}
                            {currentRecipientAddress && (
                                <Stack
                                    direction="row"
                                    sx={{
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 2.5,
                                        gap: 2,
                                    }}
                                >
                                    <Box>
                                        <Typography variant="body2">Address</Typography>
                                        <MuiLink
                                            href={`https://maps.google.com/maps?q=${encodeURIComponent(
                                                currentRecipientAddress
                                            )}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="body2"
                                            color="purple.900"
                                            sx={{ textDecoration: 'underline' }}
                                        >
                                            {currentRecipientAddress}
                                        </MuiLink>
                                    </Box>
                                    <StyledIcon icon={faMapLocationDot} />
                                </Stack>
                            )}

                            {/* Repeating Days */}
                            {repeatingDays && (
                                <Stack
                                    direction="row"
                                    sx={{
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 2.5,
                                        gap: 2,
                                    }}
                                >
                                    <Box>
                                        <Typography variant="body2">Repeating Days</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {repeatingDays}
                                        </Typography>
                                    </Box>
                                    <StyledIcon icon={faRepeat} />
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            )}
            <PageFooter includeShadow>
                {isVisitCaregiver && (isAwaitingSummary || isInProgress) && (
                    <Button
                        variant="contained"
                        fullWidth
                        color="primary"
                        onClick={handleAddVisitSummary}
                    >
                        Add Visit Summary
                    </Button>
                )}
                {!isVisitCaregiver && isTeamAdmin && (
                    <Button
                        variant="contained"
                        fullWidth
                        color="primary"
                        onClick={handleMessageCaregiver}
                    >
                        Message Caregiver
                    </Button>
                )}
            </PageFooter>
        </InnerMaxWidthContainer>
    );
}
